/**
 * 🟥 CUADRO NEGRO V7.1 — AutoFixResultModal.
 *
 * Modal que muestra el resultado de POST /api/github/auto-fix-pr:
 *   - Lista de fixes aplicados (path / line / action)
 *   - Lista de items skipped (con razón)
 *   - Link al commit en GitHub
 *   - Hint sobre dispatch CI (si aplica)
 */
import {
  Wand2, X, CheckCircle2, AlertCircle, ExternalLink, GitCommit, FileEdit,
} from 'lucide-react';

const actionStyles = {
  replace: { color: 'text-amber-300', label: 'Reemplazar' },
  insert: { color: 'text-emerald-300', label: 'Insertar' },
  delete: { color: 'text-rose-300', label: 'Borrar' },
};

export default function AutoFixResultModal({ result, isEs = true, onClose }) {
  if (!result) return null;
  const applied = result.applied_fixes || [];
  const skipped = result.skipped || [];
  const filesModified = result.files_modified || 0;

  return (
    <div
      className="fixed inset-0 z-[8700] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="autofix-result-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl max-h-[88vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-indigo-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-indigo-900/40 to-violet-900/30">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-indigo-300" />
            <div>
              <h2 className="text-base font-bold text-white">{isEs ? 'Auto-Fix IA' : 'AI Auto-Fix'}</h2>
              <p className="text-[11px] text-indigo-300/80">
                {isEs
                  ? `${applied.length} fixes aplicados · ${filesModified} archivos modificados · ${skipped.length} omitidos`
                  : `${applied.length} fixes applied · ${filesModified} files modified · ${skipped.length} skipped`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="autofix-result-close"
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Commit info */}
          {result.commit_sha && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
              <GitCommit className="w-4 h-4 text-indigo-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white">{isEs ? 'Commit creado' : 'Commit created'}</div>
                <div className="text-[11px] font-mono text-indigo-300 truncate">{result.commit_sha.slice(0, 12)}…</div>
              </div>
              {result.commit_url && (
                <a
                  href={result.commit_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="autofix-commit-link"
                  className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  {isEs ? 'Ver en GitHub' : 'View in GitHub'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}

          {/* Applied list */}
          {applied.length > 0 ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> {isEs ? 'Aplicados' : 'Applied'} ({applied.length})
              </div>
              <div className="space-y-1">
                {applied.map((fx, idx) => {
                  const meta = actionStyles[fx.action] || actionStyles.replace;
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 p-2 rounded-md bg-slate-900 border border-slate-800"
                      data-testid={`autofix-applied-${idx}`}
                    >
                      <FileEdit className={`w-3.5 h-3.5 ${meta.color} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-slate-200 truncate">{fx.path}</div>
                      </div>
                      <span className="text-[10px] text-slate-500">L{fx.line}</span>
                      <span className={`text-[10px] uppercase ${meta.color} px-1.5 py-0.5 rounded bg-slate-800`}>
                        {meta.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center text-slate-500 text-sm py-6">
              {isEs
                ? 'La IA no encontró fixes para aplicar en este PR. Todo se ve bien.'
                : 'AI found no fixes to apply. PR looks good.'}
            </div>
          )}

          {/* Skipped list */}
          {skipped.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {isEs ? `Omitidos (${skipped.length})` : `Skipped (${skipped.length})`}
              </summary>
              <div className="mt-1 space-y-0.5 max-h-48 overflow-y-auto">
                {skipped.map((s, idx) => (
                  <div key={idx} className="text-[10px] font-mono text-slate-400 p-1.5 rounded bg-slate-900 border border-slate-800">
                    <span className="text-slate-200">{s.path}</span> · <span className="text-amber-300">{s.reason}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <footer className="px-5 py-2.5 border-t border-slate-800 bg-slate-900/60 text-center">
          <p className="text-[11px] text-slate-500 italic">
            {isEs
              ? '🤖 Auto-Fix IA aplica solo archivos del PR · nunca toca main · CI se dispara automáticamente si hay workflow activo.'
              : '🤖 AI Auto-Fix only modifies PR files · never main · CI dispatched automatically if workflow exists.'}
          </p>
        </footer>
      </div>
    </div>
  );
}
